import {Injectable} from '@angular/core';
import {HttpClient, HttpContext} from "@angular/common/http";
import {SUPPRESS_ERROR_HANDLER} from "../../../shared/service/crud/suppress-error-token";
import {Observable} from "rxjs";
import {MvsCrudService} from "../../../shared/service/crud/mvs-crud.service";
import {UiQlWidgetValidateRequestDto} from "../../model/dto/api/ui-ql-widget-validate-request.dto";
import {UiQlWidgetValidateResponseDto} from "../../model/dto/api/ui-ql-widget-validate-response.dto";
import {UiQlWidgetSaveRequestDto} from "../../model/dto/api/ui-ql-widget-save-request.dto";
import {UiQlWidgetSaveResponseDto} from "../../model/dto/api/ui-ql-widget-save-response.dto";
import {UiQlWidgetDefinitionDto} from "../../model/dto/api/ui-ql-widget-definition.dto";

/**
 * `UiQlWidgetController` (`api-contract.md`) — the typed QL-Widget-Builder CRUD-ish surface, next
 * to (NOT part of) the generic `ui.UiWidget` CRUD. Every endpoint here requires ui-write access
 * (403 otherwise); the backend enforces this regardless of what the FE guard does.
 *
 * Deliberately NOT the generic `UiWidgetService` (`ui/uiWidgets`) — that path accepts a raw
 * `parameterJson`/`qlQueryJson` with no policy check (the confirmed generic-CRUD-bypass gap the
 * backend Gate test closes: `PUT /mvsa/ui/uiWidgets` with a forbidden `parameters` must be
 * rejected there too). This service is the ONLY sanctioned way for the Builder/Playground to
 * create, update, probe, activate or deactivate a `dataSource=qlQuery` widget — every call round-trips
 * through `UiQlWidgetDefinitionPolicy` server-side.
 */

/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: DataProvider
 * Reason: Typed CRUD/validate service for dataSource=qlQuery ui.UiWidget definitions - the sole
 *   sanctioned data provider backend for the QL-Widget-Builder and Playground.
 */
@Injectable({
    providedIn: 'root'
})
export class UiQlWidgetService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/ui/qlWidgets');
    }

    /**
     * Policy check (and, with `probe: true`, an author-rights engine probe run with no data rows)
     * of a definition — the debounced live-feedback call while editing in Builder/Playground.
     *
     * @param request `qlQuery` MUST come from `QlBuilderQueryConverter.toQlQuery(...)`.
     */
    public validate(request: UiQlWidgetValidateRequestDto): Observable<UiQlWidgetValidateResponseDto> {

        return this.http.post<UiQlWidgetValidateResponseDto>(
            `${this.apiUrl}/validate`,
            request
        );
    }

    /**
     * Creates a new QL widget. Alias is derived from `name` (`ui.ql.{slug}`) unless given
     * explicitly; a collision on an existing alias is refused with `409`.
     *
     * Named `createDefinition`, NOT `create` — `MvsCrudService.create(dto: DtoDetail, ...)` already
     * owns that name with an incompatible generic-CRUD signature; overriding it here would either
     * fail to compile (TS2416) or silently break every other caller that goes through the base
     * class's generic `create`.
     */
    public createDefinition(request: UiQlWidgetSaveRequestDto): Observable<UiQlWidgetSaveResponseDto> {

        return this.http.post<UiQlWidgetSaveResponseDto>(
            this.apiUrl,
            request
        );
    }

    /**
     * Updates an existing QL widget (name/description/definition/sizes). `request.alias`, if sent
     * at all, MUST match the current alias — the backend refuses a change with `400 aliasImmutable`.
     *
     * Named `updateDefinition` for the same reason as {@link createDefinition} (`MvsCrudService.update`
     * collision).
     */
    public updateDefinition(uiWidgetId: number, request: UiQlWidgetSaveRequestDto): Observable<UiQlWidgetSaveResponseDto> {

        return this.http.put<UiQlWidgetSaveResponseDto>(
            `${this.apiUrl}/${uiWidgetId}`,
            request
        );
    }

    /**
     * The full round-trip shape of one QL widget, for editing in Builder or re-import into
     * Playground.
     *
     * Named `getDefinition` for the same reason as {@link createDefinition} (`MvsCrudService.get`
     * collision).
     */
    public getDefinition(uiWidgetId: number): Observable<UiQlWidgetDefinitionDto> {

        return this.http.get<UiQlWidgetDefinitionDto>(
            `${this.apiUrl}/${uiWidgetId}`
        );
    }

    /**
     * Deactivates the widget (`UiHomeApp.hidden = true`) rather than deleting it — placed tiles
     * keep referring to it, they simply stop being offered from the library. `204`, no body.
     * Without the global error toast: the builder reports 403/404 itself in German.
     */
    public deactivateDefinition(uiWidgetId: number): Observable<void> {

        return this.http.put<void>(
            `${this.apiUrl}/${uiWidgetId}/deactivate`,
            null,
            {context: UiQlWidgetService.suppressErrorToast()}
        );
    }

    /**
     * Counterpart of {@link deactivateDefinition}: re-activates the widget (`UiHomeApp.hidden = false`)
     * so the library offers it again. `204`, no body.
     */
    public activateDefinition(uiWidgetId: number): Observable<void> {

        return this.http.put<void>(
            `${this.apiUrl}/${uiWidgetId}/activate`,
            null,
            {context: UiQlWidgetService.suppressErrorToast()}
        );
    }

    /** The builder reports activate/deactivate errors itself - no technical toast on top. */
    private static suppressErrorToast(): HttpContext {
        return new HttpContext().set(SUPPRESS_ERROR_HANDLER, true);
    }

}
